import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Eye, Scale, ShieldCheck } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, Reveal, RevealItem, RevealList, SlideShell, useAccent } from "@/ui";

interface DefenseStep {
  step: string;
  heading: string;
  icon: LucideIcon;
  lines: string[];
  /** Emphasised takeaway at the bottom of the card. */
  takeaway: string;
}

const STEPS: DefenseStep[] = [
  {
    step: "01 · Detect",
    heading: "Watch for the signals",
    icon: Eye,
    lines: [
      "Sudden backlink spikes",
      "Odd anchor text",
      "Hacked content · fake reviews",
    ],
    takeaway: "Document with dates and exports.",
  },
  {
    step: "02 · Harden",
    heading: "Close the doors",
    icon: ShieldCheck,
    lines: [
      "Harden hosting, CMS, and forms",
      "Fix malware fast",
    ],
    takeaway: "Use Search Console messages and security reports.",
  },
  {
    step: "03 · Respond",
    heading: "Respond proportionately",
    icon: Scale,
    lines: [
      "Disavow is uncommon",
      "Google often ignores obvious spam links",
    ],
    takeaway: "Legal and platform-abuse channels exist for defamation and copyright.",
  },
];

/** c6-negative-seo — recognize attacks, protect the site. */
export function NegativeSeoSlide({ active }: SlideProps) {
  const [from] = useAccent();
  return (
    <SlideShell chapter={6} title="Negative SEO: recognize and protect your site">
      <div className="flex w-full flex-col gap-8">
        <RevealList active={active} stagger={0.16} className="grid grid-cols-3 gap-8">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <RevealItem key={s.step} className="h-full">
                <GlassCard className="flex h-full flex-col gap-5 p-9">
                  <div className="flex items-center justify-between">
                    <span
                      className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5"
                    >
                      <Icon size={34} strokeWidth={1.75} style={{ color: from }} />
                    </span>
                    <span
                      className="text-xl font-medium uppercase tracking-widest"
                      style={{ color: from }}
                    >
                      {s.step}
                    </span>
                  </div>
                  <h2 className="font-display text-3xl font-semibold text-white">
                    {s.heading}
                  </h2>
                  <ul className="flex flex-col gap-3">
                    {s.lines.map((line) => (
                      <li
                        key={line}
                        className="flex items-start gap-3 text-2xl leading-snug text-white/80"
                      >
                        <span
                          className="mt-3.5 h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: from }}
                        />
                        {line}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-auto border-t border-white/10 pt-4 text-2xl font-semibold leading-snug text-white">
                    {s.takeaway}
                  </p>
                </GlassCard>
              </RevealItem>
            );
          })}
        </RevealList>

        {/* Ethics strip */}
        <Reveal active={active} delay={0.65}>
          <div className="flex items-center gap-5 rounded-2xl border border-accent-rose/40 bg-accent-rose/10 px-8 py-5">
            <AlertTriangle className="h-8 w-8 shrink-0 text-accent-rose" strokeWidth={1.75} />
            <p className="text-2xl leading-snug text-white/90">
              This is about <span className="font-semibold text-white">defense</span> —
              harming others' sites is{" "}
              <span className="font-semibold text-accent-rose">
                unethical and often illegal
              </span>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </SlideShell>
  );
}
